import React, { useState } from 'react';
import { PageHeader, Card, FormGroup, Input, Select, Button, DataTable, ActionButtons, SectionDivider, Badge } from '../../components/common/UIComponents';
import { BankAccountController } from '../../controllers';
import { AccountHeads } from '../../models';
import { useAsyncData } from '../../hooks/useAsyncData';

const EMPTY = { accountName:'', accountNumber:'', branchName:'', ifscCode:'', openingBalance:'', accountHead:'', address:'', phone:'', email:'', contactPerson:'' };

export default function BankCashMasterPage() {
  const { data: records, loading, error, reload } = useAsyncData(() => BankAccountController.getAll());
  const [accountType, setAccountType] = useState('BANK');
  const [form, setForm]   = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);

  const handleSubmit = async () => {
    if (!form.accountName) return;
    setSaving(true);
    try {
      const payload = { ...form, accountType, openingBalance: parseFloat(form.openingBalance) || 0 };
      if (editing) { await BankAccountController.update(editing, payload); setEditing(null); }
      else { await BankAccountController.add(payload); }
      setForm(EMPTY); reload();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: 'id', label: '#' },
    { key: 'accountName',   label: 'Bank Name',    render: r => r.accountName || r.name },
    { key: 'accountNumber', label: 'Account No',   className: 'td-mono', render: r => r.accountNumber || r.accountNo },
    { key: 'branchName',    label: 'Branch',       render: r => r.branchName || r.branch },
    { key: 'openingBalance', label: 'Opening Balance', className: 'td-mono', render: row => (
      <span className={`td-amount ${(row.openingBalance||0) >= 0 ? 'credit' : 'debit'}`}>
        ₹{Math.abs(row.openingBalance||0).toLocaleString('en-IN')}
      </span>
    )},
    { key: 'accountType', label: 'Type', render: row => <Badge color={(row.accountType||row.type) === 'BANK' ? 'blue' : 'green'}>{row.accountType || row.type}</Badge> },
    { key: 'actions', label: 'Actions', render: row => (
      <ActionButtons
        onEdit={() => { setForm({ accountName: row.accountName||row.name||'', accountNumber: row.accountNumber||row.accountNo||'', branchName: row.branchName||row.branch||'', ifscCode: row.ifscCode||row.ifsc||'', openingBalance: (row.openingBalance||'').toString(), accountHead: row.accountHead||row.head||'', address: row.address||'', phone: row.phone||'', email: row.email||'', contactPerson: row.contactPerson||row.contact||'' }); setAccountType(row.accountType||row.type||'BANK'); setEditing(row.id); }}
        onDelete={async () => { try { await BankAccountController.delete(row.id); reload(); } catch(e){alert(e.message);} }}
      />
    )},
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Bank/Cash Master" description="Manage all bank accounts and cash registers" />
      <Card title={editing ? 'Edit Bank/Cash Account' : 'Add New Bank/Cash Account'} style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 14 }}>
          <div className="radio-group">
            <label className="radio-label"><input type="radio" name="acType" checked={accountType === 'BANK'} onChange={() => setAccountType('BANK')} /> Bank</label>
            <label className="radio-label"><input type="radio" name="acType" checked={accountType === 'CASH'} onChange={() => setAccountType('CASH')} /> Cash</label>
          </div>
        </div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 14 }}>
          <FormGroup label="Bank/Cash A/c Name" required>
            <Input placeholder="Enter Bank Name" value={form.accountName} onChange={e => setForm(p=>({...p,accountName:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Account No">
            <Input placeholder="Enter Account No" value={form.accountNumber} onChange={e => setForm(p=>({...p,accountNumber:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Branch Name">
            <Input placeholder="Enter Branch Name" value={form.branchName} onChange={e => setForm(p=>({...p,branchName:e.target.value}))} />
          </FormGroup>
          <FormGroup label="IFSC Code">
            <Input placeholder="Enter IFSC Code" value={form.ifscCode} onChange={e => setForm(p=>({...p,ifscCode:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Opening Balance">
            <Input type="number" placeholder="0.00" value={form.openingBalance} onChange={e => setForm(p=>({...p,openingBalance:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Account Head">
            <Select value={form.accountHead} onChange={e => setForm(p=>({...p,accountHead:e.target.value}))} options={AccountHeads} placeholder="Select" />
          </FormGroup>
          <FormGroup label="Address">
            <Input placeholder="Enter Address" value={form.address} onChange={e => setForm(p=>({...p,address:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Phone">
            <Input placeholder="Contact No" value={form.phone} onChange={e => setForm(p=>({...p,phone:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Email">
            <Input placeholder="Email Id" value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))} />
          </FormGroup>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update Account' : 'Add Account'}</Button>
          <Button variant="secondary" onClick={() => { setForm(EMPTY); setEditing(null); }}>Clear</Button>
        </div>
      </Card>
      <SectionDivider />
      {loading && <div style={{ padding:20, color:'#78909c' }}>Loading…</div>}
      {error   && <div style={{ padding:20, color:'#c62828' }}>Error: {error}</div>}
      {!loading && <DataTable title="Bank Book Details" columns={columns} data={records} />}
    </div>
  );
}
